import {Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, Output, EventEmitter} from '@angular/core';
import {SurveyService} from '../../../services/surveys.service';
import {TranslateService} from '@ngx-translate/core';
import {ScriptLoaderService} from 'angular-google-charts';
import {ResponseTypesService} from '../../../services/config/response-types.service';
import {MatDialog} from '@angular/material/dialog';
import {UserService} from '../../../services/user.service';
import {ToastService} from '../../../services/general/toast.service';
import {AiService} from '../../../services/ai.service';
import {lowerCase} from 'lodash';
import {StatementGeneratorService} from '../../../services/statement-generator.service';

@Component({
    selector: 'app-single-question-ai-analysis',
    templateUrl: './single-question-ai-analysis.component.html',
    styleUrls: ['./single-question-ai-analysis.component.scss'],
    standalone: false
})
export class SingleQuestionAiAnalysisComponent implements OnInit, OnDestroy, OnChanges {
  @Input() type!: any;
  @Input() object!: any;
  @Input() question!: any;
  @Input() surveyId!: number;
  @Output() analysisCompleted = new EventEmitter<void>();

  constructor(
    public surveyService: SurveyService,
    public translateService: TranslateService,
    private loaderService: ScriptLoaderService,
    private responsesTypeService: ResponseTypesService,
    private dialog: MatDialog,
    private userService: UserService,
    private toast: ToastService,
    private aiService: AiService,
    private statementGeneratorService: StatementGeneratorService,
  ) {
  }

  loading = false;
  analysis: any = null;
  model: any = null;

  // Nuove proprietà per gestione analisi AI
  aiAnalyses: any = null;
  selectedAnalysisType: 'super-short' | 'short' | 'full' = 'short';
  isPolling = false;
  pollingInterval: any = null;
  pollingAttempts = 0;
  maxPollingAttempts = 120; // 120 tentativi × 3 secondi = 6 minuti
  analysisError = false;

  // Survey data con attachments
  surveyData: any = null;

  // Attachment processing status
  attachmentsProcessing = false;
  attachmentPollingInterval: any = null;
  processingMessage = '';

  // Processed attachments LLM analysis
  processedAttachments: any[] = [];
  showAttachments = false;
  loadingAttachments = false;
  expandedAttachmentId: number | null = null;
  displayedColumns: string[] = ['name', 'type', 'level', 'status', 'date', 'analysis'];
  hasAttachmentsFlag = false;

  // Rewrite statement
  rewritingStatement = false;
  rewrittenStatement: string | null = null;

  ngOnInit(): void {

    switch (this.type) {
      case 'decision_table':
        const proposal = this.object?.proposals?.find((proposal: any) => proposal?.criteria?.find((criterion: any) => criterion?.id === this.question?.id));
        this.model = {
          type: 'DECISION_TABLE',
          decision_table_title: this.object.decision_name,
          decision_table_description: JSON.stringify(this.object.problem_statement),
          decision_table_statement_title: this.question?.title,
          decision_table_statement_description: this.question?.value,
          decision_table_new_or_reviewed: proposal?.evaluation_session,
          certainty_perc: this.question?.results?.certainty_degree,
          contraddiction_perc: this.question?.results?.contraddiction_degree,
          responses: JSON.stringify(this.question?.response)
        };
        break;
      case 'survey':
        // Fetch survey details to get attachments
        if (this.surveyId) {
          this.surveyService.surveyDetails(this.surveyId.toString(), (survey) => {
            this.surveyData = survey;

            // Trova la domanda corrente nell'array delle domande del survey
            const currentQuestion = survey?.questions?.find((q: any) => q.id === this.question?.id);

            // Survey attachments
            const surveyPubmedAttachments = survey?.attachments?.filter((a: any) => a.type === 'PUBMED') || [];
            const surveyFileAttachments = survey?.attachments?.filter((a: any) => a.type === 'FILE') || [];

            // Question attachments
            const questionPubmedAttachments = currentQuestion?.attachment_pubmeds || [];
            const questionFileAttachments = currentQuestion?.attachment_files || [];

          });

          // Check for processing attachments
          this.checkAttachmentProcessingStatus();

          // Check if there are any attachments to show the button
          this.checkIfHasAttachments();
        }

        this.loadAiAnalysis();
        break;
      default:
        break;
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.stopAttachmentPolling();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Quando cambia la domanda (es. passando alla domanda successiva)
    if (changes['question'] && !changes['question'].firstChange && this.type === 'survey') {

      // Reset dello stato
      this.aiAnalyses = null;
      this.loading = false;
      this.analysisError = false;
      this.stopPolling();

      // Reset attachment processing state
      this.attachmentsProcessing = false;
      this.processingMessage = '';
      this.stopAttachmentPolling();

      // Reset processed attachments
      this.processedAttachments = [];
      this.showAttachments = false;
      this.hasAttachmentsFlag = false;

      // Check attachment status for the new question
      if (this.surveyId) {
        this.checkAttachmentProcessingStatus();
        this.checkIfHasAttachments();
      }

      // Ricarica l'analisi per la nuova domanda
      this.loadAiAnalysis();
    }
  }


  generateAIAnalysis() {
    this.loading = true;
    if (this.type == 'survey') {
      this.aiService.surveyQuestionAIAnalisys(this.question?.id, (result) => {
        this.analysis = this.cleanText(result.response);
        this.loading = false;
      }, () => {
        this.loading = false;
      });
    } else {
      this.aiService.generateSummarySingleQ(this.model, (result) => {
        this.analysis = this.cleanText(result.response);
        this.loading = false;
      }, () => {
        this.loading = false;
      });
    }
  }

  copyText(text: any) {
    navigator.clipboard.writeText(text);
    this.toast.success(
      this.translateService.instant('generic.text-copied-successfully')
    );
  }

  cleanText(text: string): string {
    return text
      .replace(/\\n/g, '\n\n')
      .replace(/\\t/g, '    ');
  }

  /**
   * Carica l'analisi AI esistente dal database
   */
  loadAiAnalysis(): void {
    if (!this.question?.id) {
      return;
    }

    this.loading = true;

    this.aiService.getQuestionAiAnalysis(
      this.question.id,
      (result) => {
        this.aiAnalyses = result;
        this.processAnalyses();

        const complete = this.areAnalysesComplete();

        if (complete) {
          // Analisi completate, mostra i risultati
          this.loading = false;
        } else if (this.aiAnalyses?.analyses?.short || this.aiAnalyses?.analyses?.full) {
          // Analisi esistono ma non sono complete, verifica se sono in elaborazione
          const shortStatus = this.aiAnalyses?.analyses?.short?.status;
          const fullStatus = this.aiAnalyses?.analyses?.full?.status;

          if (shortStatus === 'processing' || fullStatus === 'processing') {
            // Almeno una analisi è in elaborazione, avvia il polling
            this.startPolling();
          } else {
            // Le analisi esistono ma non sono né complete né in elaborazione
            this.loading = false;
          }
        } else {
          // Nessuna analisi trovata
          this.loading = false;
        }
      },
      (error) => {
        this.loading = false;
      }
    );
  }

  /**
   * Metodo chiamato quando l'utente clicca il bottone "Analisi AI"
   */
  onGenerateAiAnalysisClick(): void {
    this.loading = true;
    this.analysisError = false;

    // Prima prova a caricare dal database
    this.aiService.getQuestionAiAnalysis(
      this.question.id,
      (result) => {
        this.aiAnalyses = result;
        this.processAnalyses();

        // Verifica se le analisi sono complete
        if (this.areAnalysesComplete()) {
          this.loading = false;
        } else {
          // Le analisi non sono complete, triggera la generazione
          this.triggerAnalysisGeneration();
        }
      },
      (error) => {
        // L'analisi non esiste, triggera la generazione
        this.triggerAnalysisGeneration();
      }
    );
  }

  /**
   * Elabora le analisi dal backend per normalizzare la struttura
   */
  processAnalyses(): void {
    if (!this.aiAnalyses) {
      return;
    }

    // Se la risposta è già nel formato corretto (con analyses.short e analyses.full)
    if (this.aiAnalyses.analyses?.short && this.aiAnalyses.analyses?.full) {
      return;
    }

    // Se la risposta è un array di analisi
    if (Array.isArray(this.aiAnalyses)) {
      const analyses = this.aiAnalyses;

      // Trova l'ultima analisi di tipo "short" con status "completed"
      const shortAnalyses = analyses
        .filter((a: any) => a.type === 'short' && a.status === 'completed')
        .sort((a: any, b: any) => {
          const dateA = new Date(a.updated_at || a.created_at).getTime();
          const dateB = new Date(b.updated_at || b.created_at).getTime();
          return dateB - dateA; // Ordine decrescente (più recente prima)
        });

      // Trova l'ultima analisi di tipo "full" con status "completed"
      const fullAnalyses = analyses
        .filter((a: any) => a.type === 'full' && a.status === 'completed')
        .sort((a: any, b: any) => {
          const dateA = new Date(a.updated_at || a.created_at).getTime();
          const dateB = new Date(b.updated_at || b.created_at).getTime();
          return dateB - dateA;
        });

      // Trasforma in formato normalizzato
      this.aiAnalyses = {
        analyses: {
          short: shortAnalyses[0] || null,
          full: fullAnalyses[0] || null
        }
      };

    } else if (this.aiAnalyses.analyses && Array.isArray(this.aiAnalyses.analyses)) {
      // Se la risposta ha un array in analyses
      const analyses = this.aiAnalyses.analyses;

      const shortAnalyses = analyses
        .filter((a: any) => a.type === 'short' && a.status === 'completed')
        .sort((a: any, b: any) => {
          const dateA = new Date(a.updated_at || a.created_at).getTime();
          const dateB = new Date(b.updated_at || b.created_at).getTime();
          return dateB - dateA;
        });

      const fullAnalyses = analyses
        .filter((a: any) => a.type === 'full' && a.status === 'completed')
        .sort((a: any, b: any) => {
          const dateA = new Date(a.updated_at || a.created_at).getTime();
          const dateB = new Date(b.updated_at || b.created_at).getTime();
          return dateB - dateA;
        });

      this.aiAnalyses = {
        analyses: {
          short: shortAnalyses[0] || null,
          full: fullAnalyses[0] || null
        }
      };

    }
  }

  /**
   * Verifica se le analisi short e full sono complete
   */
  areAnalysesComplete(): boolean {
    // Il polling si ferma quando entrambe le analisi hanno status "completed"
    const complete = (
      this.aiAnalyses?.analyses?.short?.status === 'completed' &&
      this.aiAnalyses?.analyses?.full?.status === 'completed'
    );


    return complete;
  }

  /**
   * Triggera la generazione dell'analisi AI
   */
  triggerAnalysisGeneration(): void {

    if (!this.surveyId || !this.question?.id) {
      this.loading = false;
      this.analysisError = true;
      this.toast.error(
        this.translateService.instant('generic.error'),
        'Survey ID or Question ID not found!'
      );
      return;
    }

    // Trova la domanda corrente nell'array delle domande del survey
    const currentQuestion = this.surveyData?.questions?.find((q: any) => q.id === this.question?.id);

    // Prepara gli allegati del survey
    const surveyAttachments = this.surveyData?.attachments || [];

    // Prepara gli allegati della domanda (attachment_files e attachment_pubmeds)
    const questionFileAttachments = currentQuestion?.attachment_files || [];
    const questionPubmedAttachments = currentQuestion?.attachment_pubmeds || [];

    // Combina tutti gli allegati della domanda in un unico array
    const questionAttachments = [
      ...questionFileAttachments.map((file: any) => ({ ...file, type: 'FILE' })),
      ...questionPubmedAttachments.map((pubmed: any) => ({ ...pubmed, type: 'PUBMED' }))
    ];

    // Fetch LLM analysis for all attachments
    this.surveyService.getAttachmentsLLMAnalysis(
      this.surveyId.toString(),
      this.question.id.toString(),
      undefined,
      (attachmentsResult) => {
        // Parse the result
        let allAttachments: any[] = [];
        if (Array.isArray(attachmentsResult)) {
          allAttachments = attachmentsResult;
        } else if (attachmentsResult && typeof attachmentsResult === 'object') {
          allAttachments = attachmentsResult.data || attachmentsResult.attachments || attachmentsResult.results || [];
        }

        // Separate attachments by level
        const surveyLlmAnalysis = allAttachments.filter((a: any) => a.level === 'SURVEY');
        const questionLlmAnalysis = allAttachments.filter((a: any) => a.level === 'QUESTION');
        const answerLlmAnalysis = allAttachments.filter((a: any) => a.level === 'ANSWER');

        // Trigger AI analysis with LLM analysis data
        this.aiService.triggerSurveyAiAnalysis(
          this.surveyId,
          this.question.id,
          surveyAttachments,
          questionAttachments,
          surveyLlmAnalysis,
          questionLlmAnalysis,
          answerLlmAnalysis,
          (result) => {
            this.startPolling();
          },
          (error) => {
            this.loading = false;
            this.analysisError = true;
          }
        );
      },
      (error) => {
        console.error('Error fetching LLM analysis:', error);
        // Continue without LLM analysis if fetch fails
        this.aiService.triggerSurveyAiAnalysis(
          this.surveyId,
          this.question.id,
          surveyAttachments,
          questionAttachments,
          [],
          [],
          [],
          (result) => {
            this.startPolling();
          },
          (error) => {
            this.loading = false;
            this.analysisError = true;
          }
        );
      }
    );
  }

  /**
   * Avvia il polling per verificare il completamento dell'analisi
   */
  startPolling(): void {
    this.isPolling = true;
    this.pollingAttempts = 0;

    this.pollingInterval = setInterval(() => {
      this.pollingAttempts++;

      // Recupera lo stato dell'analisi
      this.aiService.getQuestionAiAnalysis(
        this.question.id,
        (result) => {

          this.aiAnalyses = result;
          this.processAnalyses();

          // Verifica se le analisi sono complete
          if (this.areAnalysesComplete()) {

            this.stopPolling();
            this.loading = false;

            // Emetti evento per passare alla domanda successiva
            this.analysisCompleted.emit();
            return;
          }

          // Verifica se ci sono errori
          if (
            this.aiAnalyses?.analyses?.short?.status === 'failed' ||
            this.aiAnalyses?.analyses?.full?.status === 'failed'
          ) {

            this.stopPolling();
            this.loading = false;
            this.analysisError = true;
            this.toast.error(
              this.translateService.instant('generic.error'),
              this.translateService.instant('generic.analysis-failed')
            );
            return;
          }


        },
        (error) => {
          // Continua il polling anche in caso di errore temporaneo
        }
      );
    }, 3000); // Polling ogni 3 secondi
  }

  /**
   * Ferma il polling
   */
  stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.isPolling = false;
  }

  /**
   * Restituisce l'analisi attualmente selezionata
   */
  get currentAnalysis(): string {
    if (!this.aiAnalyses?.analyses) {
      return '';
    }

    switch (this.selectedAnalysisType) {
      case 'super-short':
        return this.getSuperShortAnalysis();
      case 'short':
        return this.aiAnalyses.analyses.short?.result?.text || '';
      case 'full':
        return this.aiAnalyses.analyses.full?.result?.text || '';
      default:
        return '';
    }
  }

  /**
   * Restituisce la data dell'ultima elaborazione dell'analisi
   */
  get lastAnalysisDate(): Date | null {
    if (!this.aiAnalyses?.analyses) {
      return null;
    }

    // Prende la data più recente tra short e full
    const shortDate = this.aiAnalyses.analyses.short?.updated_at || this.aiAnalyses.analyses.short?.created_at;
    const fullDate = this.aiAnalyses.analyses.full?.updated_at || this.aiAnalyses.analyses.full?.created_at;

    if (!shortDate && !fullDate) {
      return null;
    }

    if (!shortDate) {
      return new Date(fullDate);
    }

    if (!fullDate) {
      return new Date(shortDate);
    }

    // Restituisce la più recente
    const shortDateTime = new Date(shortDate).getTime();
    const fullDateTime = new Date(fullDate).getTime();

    return new Date(shortDateTime > fullDateTime ? shortDate : fullDate);
  }

  /**
   * Genera l'analisi super-short (prime 1-2 frasi/paragrafi della short)
   */
  getSuperShortAnalysis(): string {
    const shortContent = this.aiAnalyses?.analyses?.short?.result?.text || '';
    if (!shortContent) {
      return '';
    }

    // Prende i primi due paragrafi o le prime due frasi
    const paragraphs = shortContent.split('\n\n');
    if (paragraphs.length >= 2) {
      return paragraphs.slice(0, 2).join('\n\n');
    }

    // Se non ci sono abbastanza paragrafi, prova con le frasi
    const sentences = shortContent.split('. ');
    if (sentences.length >= 2) {
      return sentences.slice(0, 2).join('. ') + '.';
    }

    return shortContent;
  }

  /**
   * Cambia il tipo di analisi selezionato
   */
  onAnalysisTypeChange(type: 'super-short' | 'short' | 'full'): void {
    this.selectedAnalysisType = type;
  }

  /**
   * Getter di debug per capire perché il bottone non viene mostrato
   */
  get shouldShowButton(): boolean {
    const result = !this.loading && !this.analysisError && (!this.aiAnalyses || !this.areAnalysesComplete()) && !this.attachmentsProcessing;

    return result;
  }

  /**
   * Check if attachments are currently being processed
   */
  checkAttachmentProcessingStatus(): void {
    if (!this.surveyId) {
      return;
    }

    this.surveyService.checkProcessingAttachments(
      this.surveyId.toString(),
      (result) => {
        // Response format: { has_processing_attachments: boolean, processing_count: number, state_breakdown: {...}, records: [...] }
        const hasProcessing = result?.has_processing_attachments === true;
        const stateBreakdown = result?.state_breakdown || {};

        if (hasProcessing) {
          this.attachmentsProcessing = true;

          // Create detailed message
          const waitingCount = stateBreakdown.waiting || 0;
          const processingActiveCount = stateBreakdown.processing || 0;

          if (waitingCount > 0 && processingActiveCount > 0) {
            this.processingMessage = `AI attachment analysis in progress: ${processingActiveCount} processing, ${waitingCount} waiting`;
          } else if (processingActiveCount > 0) {
            this.processingMessage = `AI attachment analysis in progress: ${processingActiveCount} processing`;
          } else if (waitingCount > 0) {
            this.processingMessage = `AI attachment analysis: ${waitingCount} waiting`;
          } else {
            this.processingMessage = 'AI attachment analysis in progress';
          }

          // Start polling every 2 minutes (120000 ms)
          this.startAttachmentPolling();
        } else {
          this.attachmentsProcessing = false;
          this.processingMessage = '';
          this.stopAttachmentPolling();
        }
      },
      (error) => {
        console.error('Error checking attachment processing status:', error);
        // On error, don't block the UI
        this.attachmentsProcessing = false;
      }
    );
  }

  /**
   * Start polling for attachment processing status every 2 minutes
   */
  startAttachmentPolling(): void {
    // Clear any existing interval
    this.stopAttachmentPolling();

    // Poll every 2 minutes (120000 ms)
    this.attachmentPollingInterval = setInterval(() => {
      this.checkAttachmentProcessingStatus();
    }, 120000);
  }

  /**
   * Stop attachment processing polling
   */
  stopAttachmentPolling(): void {
    if (this.attachmentPollingInterval) {
      clearInterval(this.attachmentPollingInterval);
      this.attachmentPollingInterval = null;
    }
  }

  /**
   * Load all processed attachments LLM analysis for the current question
   *
   * This retrieves ALL processed attachments at three levels:
   * 1. Survey-level attachments (associated with survey_id)
   * 2. Question-level attachments (associated with survey_question_id)
   * 3. Answer-level attachments (associated with all answers to the current question)
   *
   * The backend endpoint returns all these attachments in a single response
   */
  loadProcessedAttachments(): void {
    if (!this.question?.id || !this.surveyId) {
      console.error('Missing surveyId or question.id');
      return;
    }

    this.loadingAttachments = true;

    // Call endpoint to get all processed attachments:
    // - Survey attachments (survey_id)
    // - Question attachments (survey_question_id)
    // - Answer attachments (all answers to this question)
    this.surveyService.getAttachmentsLLMAnalysis(
      this.surveyId.toString(),
      this.question.id.toString(),
      undefined, // survey_question_answer_id is optional - backend returns all answer attachments
      (result) => {

        // Handle different response formats
        if (Array.isArray(result)) {
          this.processedAttachments = result;
        } else if (result && typeof result === 'object') {
          // If result has a data or attachments property
          this.processedAttachments = result.data || result.attachments || result.results || [];
        } else {
          this.processedAttachments = [];
        }

        this.showAttachments = true;
        this.loadingAttachments = false;
      },
      (error) => {
        console.error('Error loading processed attachments:', error);
        this.loadingAttachments = false;
        this.processedAttachments = [];
        this.toast.error(
          this.translateService.instant('generic.error'),
          'Failed to load processed attachments'
        );
      }
    );
  }

  /**
   * Toggle attachments display
   */
  toggleAttachments(): void {
    if (this.showAttachments) {
      this.showAttachments = false;
      this.expandedAttachmentId = null; // Reset expansion when hiding
    } else {
      if (this.processedAttachments.length === 0) {
        this.loadProcessedAttachments();
      } else {
        this.showAttachments = true;
      }
    }
  }

  /**
   * Toggle the expansion of an attachment's analysis
   */
  toggleAnalysisExpansion(attachmentId: number): void {
    if (this.expandedAttachmentId === attachmentId) {
      this.expandedAttachmentId = null;
    } else {
      this.expandedAttachmentId = attachmentId;
    }
  }

  /**
   * Check if there are any attachments by calling the endpoint
   */
  checkIfHasAttachments(): void {
    if (!this.question?.id || !this.surveyId) {
      this.hasAttachmentsFlag = false;
      return;
    }

    this.surveyService.getAttachmentsLLMAnalysis(
      this.surveyId.toString(),
      this.question.id.toString(),
      undefined,
      (result) => {
        let attachments: any[] = [];
        if (Array.isArray(result)) {
          attachments = result;
        } else if (result && typeof result === 'object') {
          attachments = result.data || result.attachments || result.results || [];
        }

        this.hasAttachmentsFlag = attachments.length > 0;
      },
      (error) => {
        console.error('Error checking attachments:', error);
        this.hasAttachmentsFlag = false;
      }
    );
  }

  /**
   * Check if there are any attachments (survey or question level)
   * to determine if "View Attachments" button should be displayed
   */
  get hasAttachments(): boolean {
    return this.hasAttachmentsFlag;
  }

  rewriteStatement(): void {
    if (!this.question?.id || !this.surveyId) {
      return;
    }
    this.rewritingStatement = true;
    this.rewrittenStatement = null;
    this.statementGeneratorService.rewriteStatement(
      this.question.id,
      this.surveyId,
      (result) => {
        this.rewrittenStatement = result.rewritten_statement;
        this.rewritingStatement = false;
      },
      () => {
        this.rewritingStatement = false;
        this.toast.error(
          this.translateService.instant('generic.error'),
          this.translateService.instant('components.survey-all-responses.rewrite-statement-error')
        );
      }
    );
  }
}
